/**
 * UserLibraryContext - Optimistic UI Central State Store
 *
 * Implements the Optimistic UI Architecture Specification (docs/OPTIMISTIC_UI_SPEC.md)
 *
 * Features:
 * - Server is ALWAYS canonical
 * - Optimistic updates with snapshot-based rollback
 * - Pending mutation tracking with supersede logic
 * - Soft sync / hard rehydrate based on staleness
 * - Cross-tab communication via BroadcastChannel
 * - Full observability via libraryLogger
 */

import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  type ReactNode,
  useSyncExternalStore,
} from 'react';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { useLists } from './ListsContext';
import { libraryLog } from '../lib/libraryLogger';
import { queryKeys } from '../lib/queryKeys';
import { enqueueIntent, enqueueFollowIntent, removeIntent } from '../lib/outbox';
import type { FollowUser, FollowStatusData } from '../hooks/useUserFollowsQuery';
import {
  invalidateAllListItemsQueries,
  setAllListItemsDataWithSortInfo,
  cancelAllListItemsQueries,
  type WatchlistMeta,
  type WatchlistItemRow,
} from '../hooks/useListsQuery';

// =============================================================================
// CONSTANTS (from spec Section 2)
// =============================================================================

const MUTATION_TIMEOUT_MS = 30_000; // 30s
const TAB_INACTIVE_SOFT_SYNC_MS = 30_000; // 30s
const TAB_INACTIVE_HARD_REHYDRATE_MS = 600_000; // 10min
const SYNC_INTERVAL_MS = 300_000; // 5min
const MAX_DIVERGENCE_MS = 120_000; // 2min
const SNAPSHOT_RETENTION_MS = 60_000; // 60s

const PENDING_MUTATIONS_HARD_LIMIT = 10;

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// localStorage cache constants
const LIBRARY_CACHE_VERSION = 2;
const LIBRARY_PERSIST_DEBOUNCE_MS = 250;

function getLibraryCacheKey(userId: string): string {
  return `filmple:library:v${LIBRARY_CACHE_VERSION}:${userId}`;
}

// =============================================================================
// TYPES
// =============================================================================

export type MediaType = 'movie' | 'tv';
export type MutationAction = 'add' | 'remove' | 'set';

export interface MediaKey {
  mediaId: number;
  mediaType: MediaType;
}

// Item stored in Sets (watched, watchlist, favorites)
export interface LibraryItem {
  mediaId: number;
  mediaType: MediaType;
  itemId?: string; // Backend item ID for deletion
  _placeholder?: boolean; // INV-16: injected for pagination (add intent)
  _tombstone?: boolean; // INV-16: tombstone for unhydrated remove intent
}

// TASK A: Enrichment fields for optimistic cache (matches WatchlistItemRow)
export interface MediaEnrichment {
  genre_ids?: number[];
  runtime?: number | null;
  imdb_rating?: number;
  rt_rating?: number;
  filmpl_rating?: number;
  popularity?: number;
  original_language?: string;
  vote_count?: number;
}

// Custom list metadata
export interface CustomListMeta {
  id: string;
  name: string;
  slug: string;
  item_count: number;
  is_default: boolean;
  nextCursor?: string | null; // For pagination
}

// Mutation key format: "{action}:{mediaType}:{mediaId}" or "list:{listId}:{mediaType}:{mediaId}"
export type MutationKey = string;

export interface StateSnapshot {
  capturedAt: number;
  mutationKey: MutationKey;
  watched?: boolean;
  watchlist?: boolean;
  favorites?: boolean;
  rating?: number | null;
  inList?: boolean;
}

export interface PendingMutation {
  id: string; // UUID v4
  key: MutationKey;
  action: MutationAction;
  createdAt: number;
  snapshot: StateSnapshot;
  abortController: AbortController;
  isPlaceholder?: boolean; // INV-16: add intent for unhydrated entity
  isTombstone?: boolean; // INV-16: remove intent for unhydrated entity
}

export interface LibraryState {
  userId: string | null;
  isHydrated: boolean;
  defaultListIds: { watched: string | null; watchlist: string | null; favorites: string | null } | null;
  watched: Map<string, LibraryItem>; // key: "movie:123" or "tv:456"
  watchlist: Map<string, LibraryItem>;
  favorites: Map<string, LibraryItem>;
  ratings: Map<string, number>; // key: "movie:123", value: rating
  customLists: Map<string, CustomListMeta>; // key: listId -> metadata
  customListItems: Map<string, LibraryItem>; // key: "listId:mediaType:mediaId" -> item
  followingUsers: Set<string>; // user IDs the current user follows
  lastSyncedAt: number | null;
  lastMutationTimeForKey: Map<string, number>; // INV-3: per-key tracking
}

export interface UserLibraryContextValue {
  // State
  isHydrated: boolean;
  userId: string | null;
  defaultListIds: { watched: string | null; watchlist: string | null; favorites: string | null } | null;

  // Status checks (instant, synchronous)
  isWatched: (mediaId: number, mediaType: MediaType) => boolean;
  isInWatchlist: (mediaId: number, mediaType: MediaType) => boolean;
  isInFavorites: (mediaId: number, mediaType: MediaType) => boolean;
  getRating: (mediaId: number, mediaType: MediaType) => number | null;
  getItemId: (category: 'watched' | 'watchlist' | 'favorites', mediaId: number, mediaType: MediaType) => string | undefined;

  // Derived counts (for ProfilePage display)
  watchedCount: number;
  watchlistCount: number;
  favoritesCount: number;
  getCustomListItemCount: (listId: string) => number;

  // Bounded read-only getters (for filtering/exclusion without exposing raw Maps)
  getWatchlistIds: (mediaType?: 'movie' | 'tv', limit?: number) => number[];

  // Custom list status checks
  isInCustomList: (listId: string, mediaId: number, mediaType: MediaType) => boolean;
  getCustomLists: () => CustomListMeta[];
  getCustomListItemId: (listId: string, mediaId: number, mediaType: MediaType) => string | undefined;

  // Mutation functions (return mutation ID)
  /**
   * Toggle watched status. Returns mutation ID on success, null if superseded.
   * THROWS on failure (after rollback). Callers must use try/catch.
   * TASK A: Now accepts enrichment for instant metadata display on WatchlistPage.
   */
  toggleWatched: (mediaId: number, mediaType: MediaType, mediaTitle: string, mediaPoster?: string, mediaYear?: number, enrichment?: MediaEnrichment) => Promise<string | null>;
  /**
   * Toggle watchlist status. Returns mutation ID on success, null if superseded.
   * THROWS on failure (after rollback). Callers must use try/catch.
   * TASK A: Now accepts enrichment for instant metadata display on WatchlistPage.
   */
  toggleWatchlist: (mediaId: number, mediaType: MediaType, mediaTitle: string, mediaPoster?: string, mediaYear?: number, enrichment?: MediaEnrichment) => Promise<string | null>;
  /**
   * Toggle favorites status. Returns mutation ID on success, null if superseded.
   * THROWS on failure (after rollback). Callers must use try/catch.
   * TASK A: Now accepts enrichment for instant metadata display on WatchlistPage.
   */
  toggleFavorites: (mediaId: number, mediaType: MediaType, mediaTitle: string, mediaPoster?: string, mediaYear?: number, enrichment?: MediaEnrichment) => Promise<string | null>;
  setRating: (mediaId: number, mediaType: MediaType, rating: number) => Promise<string | null>;
  removeRating: (mediaId: number, mediaType: MediaType) => Promise<string | null>;

  // Custom list mutations
  // TASK B: Now accepts knownItemId and forceRemove for deterministic removal
  toggleCustomList: (listId: string, mediaId: number, mediaType: MediaType, mediaTitle: string, mediaPoster?: string, mediaYear?: number, knownItemId?: string, forceRemove?: boolean) => Promise<string | null>;
  fetchCustomListItems: (listId: string) => Promise<{ status: 'success' | 'error' | 'skipped' }>;
  syncCustomListItem: (listId: string, mediaId: number, mediaType: MediaType, itemId: string) => void;

  // Follow system
  isFollowing: (userId: string) => boolean;
  toggleFollow: (userId: string) => Promise<string | null>;

  // Sync controls
  softSync: () => Promise<void>;
  hardRehydrate: () => Promise<void>;

  // Pending mutation info
  hasPendingMutation: (key: MutationKey) => boolean;
  pendingMutationCount: number;
}

// =============================================================================
// HELPERS
// =============================================================================

function getMediaKey(mediaId: number, mediaType: MediaType): string {
  return `${mediaType}:${mediaId}`;
}

function getCustomListItemKey(listId: string, mediaId: number, mediaType: MediaType): string {
  return `${listId}:${mediaType}:${mediaId}`;
}

function getMutationKey(action: string, mediaId: number, mediaType: MediaType, listId?: string): MutationKey {
  if (listId) {
    return `list:${listId}:${mediaType}:${mediaId}`;
  }
  return `${action}:${mediaType}:${mediaId}`;
}

function generateMutationId(): string {
  return crypto.randomUUID();
}

// =============================================================================
// LIBRARY STORE (External to React for useSyncExternalStore)
// =============================================================================

type Listener = () => void;

class LibraryStore {
  private state: LibraryState;
  private pendingMutations: Map<MutationKey, PendingMutation>;
  private listeners: Set<Listener>;
  private timeoutIds: Map<string, ReturnType<typeof setTimeout>>;
  private syncIntervalId: ReturnType<typeof setInterval> | null;
  private lastFocusTime: number;
  private broadcastChannel: BroadcastChannel | null;

  constructor() {
    this.state = {
      userId: null,
      isHydrated: false,
      defaultListIds: null,
      watched: new Map(),
      watchlist: new Map(),
      favorites: new Map(),
      ratings: new Map(),
      customLists: new Map(),
      customListItems: new Map(),
      followingUsers: new Set(),
      lastSyncedAt: null,
      lastMutationTimeForKey: new Map(),
    };
    this.pendingMutations = new Map();
    this.listeners = new Set();
    this.timeoutIds = new Map();
    this.syncIntervalId = null;
    this.lastFocusTime = Date.now();
    this.broadcastChannel = null;

    // Set up cross-tab communication
    this.setupBroadcastChannel();

    // Set up visibility change listener
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    // Set up online event listener for sync
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline);
    }
  }

  private handleOnline = () => {
    libraryLog.syncStarted({ trigger: 'online' });
    // Sync will be triggered by the component
    this.notify();
  };

  private setupBroadcastChannel() {
    if (typeof BroadcastChannel !== 'undefined') {
      this.broadcastChannel = new BroadcastChannel('filmple:library');
      this.broadcastChannel.onmessage = (event) => {
        // INV: BroadcastChannel messages MUST be namespaced by userId
        // Handle logout: clear if the logged-out user matches THIS tab's user
        if (event.data.type === 'logout') {
          const loggedOutUserId = event.data.userId;
          if (loggedOutUserId && loggedOutUserId === this.state.userId) {
            // Same user logged out in another tab - clear this tab too
            libraryLog.authUserSwitched({
              oldUserId: this.state.userId,
              newUserId: null as unknown as string,
            });
            this.clearWithoutBroadcast(); // Avoid broadcast loop
            this.notify();
          }
        } else if (event.data.type === 'user_switched') {
          // Another tab switched users - clear if oldUserId matches THIS tab's user
          const oldUserId = event.data.oldUserId;
          if (oldUserId && oldUserId === this.state.userId) {
            libraryLog.authUserSwitched({
              oldUserId: this.state.userId,
              newUserId: event.data.newUserId,
            });
            this.clearWithoutBroadcast();
            this.notify();
          }
        } else if (event.data.type === 'mutation_confirmed' || event.data.type === 'mutation_failed') {
          // Cross-tab mutation sync - only process if for this user
          const messageUserId = event.data.userId;
          if (messageUserId !== this.state.userId) {
            // Ignore - message is for different user
            return;
          }
          // Future: sync mutation state across tabs
        } else if (event.data.type === 'follow_user') {
          // Cross-tab follow sync
          if (event.data.userId === this.state.userId && event.data.targetUserId) {
            const newFollowing = new Set(this.state.followingUsers);
            newFollowing.add(event.data.targetUserId);
            this.state = { ...this.state, followingUsers: newFollowing };
            this.notify();
          }
        } else if (event.data.type === 'unfollow_user') {
          // Cross-tab unfollow sync
          if (event.data.userId === this.state.userId && event.data.targetUserId) {
            const newFollowing = new Set(this.state.followingUsers);
            newFollowing.delete(event.data.targetUserId);
            this.state = { ...this.state, followingUsers: newFollowing };
            this.notify();
          }
        }
      };
    }
  }

  // Public method to broadcast messages (replaces direct broadcastChannel access)
  broadcast(message: { type: string; [key: string]: unknown }) {
    if (this.broadcastChannel && this.state.userId) {
      this.broadcastChannel.postMessage({ ...message, userId: this.state.userId });
    }
  }

  private handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      const inactiveTime = Date.now() - this.lastFocusTime;

      if (inactiveTime > TAB_INACTIVE_HARD_REHYDRATE_MS) {
        libraryLog.syncHardRehydrate({
          reason: 'tab_inactive',
          inactiveMs: inactiveTime,
        });
        // Hard rehydrate will be triggered by the component
      } else if (inactiveTime > TAB_INACTIVE_SOFT_SYNC_MS) {
        libraryLog.syncStarted({ trigger: 'focus' });
        // Soft sync will be triggered by the component
      }
    } else {
      this.lastFocusTime = Date.now();
    }
  };

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): LibraryState => {
    return this.state;
  };

  private notify() {
    this.listeners.forEach((listener) => listener());
  }

  // INV-6: Memory state MUST be empty when user === null
  clear() {
    // Broadcast BEFORE clearing (so other tabs know which user logged out)
    const userIdBeforeClear = this.state.userId;
    if (userIdBeforeClear) {
      this.broadcastChannel?.postMessage({ type: 'logout', userId: userIdBeforeClear });
    }
    this.clearWithoutBroadcast();
  }

  // Internal clear without broadcast (called by BroadcastChannel handler to avoid loops)
  private clearWithoutBroadcast() {
    // INV-2: Drop all pending mutations on logout
    for (const [key, mutation] of this.pendingMutations) {
      mutation.abortController.abort();
      libraryLog.mutationDropped({
        key,
        mutationId: mutation.id,
        reason: 'logout',
      });
    }

    // Clear all timeouts
    for (const timeoutId of this.timeoutIds.values()) {
      clearTimeout(timeoutId);
    }

    this.state = {
      userId: null,
      isHydrated: false,
      defaultListIds: null,
      watched: new Map(),
      watchlist: new Map(),
      favorites: new Map(),
      ratings: new Map(),
      customLists: new Map(),
      customListItems: new Map(),
      followingUsers: new Set(),
      lastSyncedAt: null,
      lastMutationTimeForKey: new Map(),
    };
    this.pendingMutations.clear();
    this.timeoutIds.clear();
  }

  setUserId(userId: string | null) {
    // INV-12: userId in state MUST match auth.user.id
    if (this.state.userId !== userId) {
      const oldUserId = this.state.userId;

      if (oldUserId !== null && userId !== null) {
        // Account switch detected - broadcast with BOTH old and new userId
        libraryLog.authUserSwitched({
          oldUserId,
          newUserId: userId,
        });
        this.broadcastChannel?.postMessage({ type: 'user_switched', oldUserId, newUserId: userId });
      }

      if (userId === null) {
        this.clear();
      } else {
        this.state = { ...this.state, userId, isHydrated: false };
      }
      this.notify();
    }
  }

  setHydrated(isHydrated: boolean) {
    this.state = { ...this.state, isHydrated };
    this.notify();
  }

  // Hydrate from server data
  hydrate(data: {
    watched: LibraryItem[];
    watchlist: LibraryItem[];
    favorites: LibraryItem[];
    ratings: Record<string, number>;
    customLists?: CustomListMeta[];
    defaultListIds?: { watched: string | null; watchlist: string | null; favorites: string | null } | null;
    syncedAt: number;
  }) {
    const watched = new Map<string, LibraryItem>();
    const watchlist = new Map<string, LibraryItem>();
    const favorites = new Map<string, LibraryItem>();
    const ratings = new Map<string, number>();
    const customLists = new Map<string, CustomListMeta>();

    data.watched.forEach((item) => {
      watched.set(getMediaKey(item.mediaId, item.mediaType), item);
    });
    data.watchlist.forEach((item) => {
      watchlist.set(getMediaKey(item.mediaId, item.mediaType), item);
    });
    data.favorites.forEach((item) => {
      favorites.set(getMediaKey(item.mediaId, item.mediaType), item);
    });
    Object.entries(data.ratings).forEach(([key, rating]) => {
      ratings.set(key, rating);
    });
    // Load custom list metadata (items fetched on-demand)
    (data.customLists || []).forEach((list) => {
      customLists.set(list.id, list);
    });

    this.state = {
      ...this.state,
      defaultListIds: data.defaultListIds ?? null,
      watched,
      watchlist,
      favorites,
      ratings,
      customLists,
      customListItems: new Map(), // Reset - items fetched on-demand
      lastSyncedAt: data.syncedAt,
      isHydrated: true,
    };

    // Clear pending mutations - they're stale after hard rehydrate
    for (const [key, mutation] of this.pendingMutations) {
      mutation.abortController.abort();
      libraryLog.mutationDropped({
        key,
        mutationId: mutation.id,
        reason: 'hard_rehydrate',
      });
    }
    this.pendingMutations.clear();
    this.state.lastMutationTimeForKey.clear();

    this.notify();
  }

  // Hydrate from localStorage cache (warm start before server hydration)
  // Does NOT abort pending mutations or clear lastMutationTimeForKey
  hydrateFromCache(cached: {
    watched: LibraryItem[];
    watchlist: LibraryItem[];
    favorites: LibraryItem[];
    ratings: Record<string, number>;
    customLists?: CustomListMeta[];
    customListItems?: Array<{ key: string; mediaId: number; mediaType: MediaType; itemId?: string }>;
    followingUsers?: string[];
    defaultListIds?: { watched: string | null; watchlist: string | null; favorites: string | null } | null;
    syncedAt: number;
  }) {
    const watched = new Map<string, LibraryItem>();
    const watchlist = new Map<string, LibraryItem>();
    const favorites = new Map<string, LibraryItem>();
    const ratings = new Map<string, number>();
    const customLists = new Map<string, CustomListMeta>();
    const customListItems = new Map<string, LibraryItem>();
    const followingUsers = new Set<string>(cached.followingUsers || []);

    cached.watched.forEach((item) => {
      watched.set(getMediaKey(item.mediaId, item.mediaType), item);
    });
    cached.watchlist.forEach((item) => {
      watchlist.set(getMediaKey(item.mediaId, item.mediaType), item);
    });
    cached.favorites.forEach((item) => {
      favorites.set(getMediaKey(item.mediaId, item.mediaType), item);
    });
    Object.entries(cached.ratings).forEach(([key, rating]) => {
      ratings.set(key, rating);
    });
    (cached.customLists || []).forEach((list) => {
      customLists.set(list.id, list);
    });

    // Rebuild customListItems from flat array with composite keys
    for (const item of cached.customListItems ?? []) {
      if (!item?.key || !item?.mediaType || typeof item?.mediaId !== 'number') continue;
      customListItems.set(item.key, {
        mediaId: item.mediaId,
        mediaType: item.mediaType,
        itemId: item.itemId,
      });
    }

    this.state = {
      ...this.state,
      defaultListIds: cached.defaultListIds ?? null,
      watched,
      watchlist,
      favorites,
      ratings,
      customLists,
      customListItems,
      followingUsers,
      lastSyncedAt: cached.syncedAt,
      isHydrated: true,
    };

    this.notify();
  }

  // Soft sync - merge server data with pending mutation protection (INV-4)
  softSync(data: {
    watched: LibraryItem[];
    watchlist: LibraryItem[];
    favorites: LibraryItem[];
    ratings: Record<string, number>;
    customLists?: CustomListMeta[];
    defaultListIds?: { watched: string | null; watchlist: string | null; favorites: string | null } | null;
    syncedAt: number;
  }) {
    const syncCategories = (
      category: 'watched' | 'watchlist' | 'favorites',
      serverItems: LibraryItem[]
    ) => {
      const currentMap = this.state[category];
      const serverMap = new Map<string, LibraryItem>();
      serverItems.forEach((item) => {
        serverMap.set(getMediaKey(item.mediaId, item.mediaType), item);
      });

      // Get pending mutations for this category with placeholder/tombstone info
      const pendingMutationsForCategory = new Map<string, PendingMutation>();
      for (const [mutKey, mutation] of this.pendingMutations) {
        if (mutKey.startsWith(category + ':')) {
          const parts = mutKey.split(':');
          pendingMutationsForCategory.set(`${parts[1]}:${parts[2]}`, mutation);
        }
      }

      // INV-3: Check staleness per key
      const newMap = new Map<string, LibraryItem>();

      // Add items from server (if not protected by pending mutation)
      for (const [key, item] of serverMap) {
        const lastMutationTime = this.state.lastMutationTimeForKey.get(`${category}:${key}`);
        const pendingMutation = pendingMutationsForCategory.get(key);

        if (lastMutationTime && data.syncedAt < lastMutationTime) {
          // Stale for this key - log and skip
          libraryLog.syncStaleIgnored({
            serverSyncedAt: data.syncedAt,
            clientLastMutation: lastMutationTime,
            deltaMs: lastMutationTime - data.syncedAt,
          });
          // Keep client state for this item
          const clientItem = currentMap.get(key);
          if (clientItem) {
            newMap.set(key, clientItem);
          }
        } else if (pendingMutation?.isTombstone) {
          // INV-16: Tombstone rule - user intended removal, don't add server item
          // User's last intent wins until mutation resolves
          continue;
        } else if (!pendingMutation) {
          // Server wins (no pending mutation)
          newMap.set(key, item);
        } else {
          // Keep client state (pending mutation, including placeholders)
          const clientItem = currentMap.get(key);
          if (clientItem) {
            newMap.set(key, clientItem);
          }
        }
      }

      // Keep items with pending mutations that server doesn't have (INV-16: placeholders)
      for (const [key, item] of currentMap) {
        const pendingMutation = pendingMutationsForCategory.get(key);
        if (pendingMutation && !newMap.has(key)) {
          // INV-16: Protect placeholder from removal during sync
          if (pendingMutation.isPlaceholder || pendingMutation.action === 'add') {
            newMap.set(key, item);
          }
        }
      }

      return newMap;
    };

    // Sync ratings with similar logic
    const syncRatings = (serverRatings: Record<string, number>) => {
      const newRatings = new Map<string, number>();
      const pendingRatingMutations = new Map<string, PendingMutation>();

      for (const [mutKey, mutation] of this.pendingMutations) {
        if (mutKey.startsWith('rating:')) {
          const parts = mutKey.split(':');
          pendingRatingMutations.set(`${parts[1]}:${parts[2]}`, mutation);
        }
      }

      // Add server ratings (if not protected)
      for (const [key, rating] of Object.entries(serverRatings)) {
        const lastMutationTime = this.state.lastMutationTimeForKey.get(`rating:${key}`);
        const pendingMutation = pendingRatingMutations.get(key);

        if (lastMutationTime && data.syncedAt < lastMutationTime) {
          // Keep client rating
          const clientRating = this.state.ratings.get(key);
          if (clientRating !== undefined) {
            newRatings.set(key, clientRating);
          }
        } else if (!pendingMutation) {
          newRatings.set(key, rating);
        } else {
          // Keep client rating
          const clientRating = this.state.ratings.get(key);
          if (clientRating !== undefined) {
            newRatings.set(key, clientRating);
          }
        }
      }

      // Keep ratings with pending mutations
      for (const [key, rating] of this.state.ratings) {
        if (pendingRatingMutations.has(key) && !newRatings.has(key)) {
          newRatings.set(key, rating);
        }
      }

      return newRatings;
    };

    // Sync custom list metadata
    const syncCustomLists = (serverLists: CustomListMeta[] = []) => {
      const newLists = new Map<string, CustomListMeta>();
      for (const list of serverLists) {
        newLists.set(list.id, list);
      }
      return newLists;
    };

    // Sync custom list items with INV-16 support
    const syncCustomListItems = () => {
      const currentItems = this.state.customListItems;
      const newItems = new Map<string, LibraryItem>();

      // Get pending custom list mutations
      const pendingCustomListMutations = new Map<string, PendingMutation>();
      for (const [mutKey, mutation] of this.pendingMutations) {
        if (mutKey.startsWith('list:')) {
          // key format: list:listId:mediaType:mediaId
          const parts = mutKey.split(':');
          if (parts.length >= 4) {
            const itemKey = `${parts[1]}:${parts[2]}:${parts[3]}`;
            pendingCustomListMutations.set(itemKey, mutation);
          }
        }
      }

      // Keep items with pending mutations (placeholders protected, tombstones removed)
      for (const [key, item] of currentItems) {
        const pendingMutation = pendingCustomListMutations.get(key);
        if (pendingMutation) {
          if (pendingMutation.isTombstone) {
            // INV-16: Tombstone - don't keep item (user intended removal)
            continue;
          }
          // Keep item (placeholder or regular pending mutation)
          newItems.set(key, item);
        } else {
          // No pending mutation - keep item (will be refreshed on next fetch)
          newItems.set(key, item);
        }
      }

      return newItems;
    };

    this.state = {
      ...this.state,
      defaultListIds: data.defaultListIds ?? this.state.defaultListIds ?? null,
      watched: syncCategories('watched', data.watched),
      watchlist: syncCategories('watchlist', data.watchlist),
      favorites: syncCategories('favorites', data.favorites),
      ratings: syncRatings(data.ratings),
      customLists: syncCustomLists(data.customLists),
      customListItems: syncCustomListItems(),
      lastSyncedAt: data.syncedAt,
    };

    this.notify();
  }

  // INV-8: A mutation MUST NOT be created if !isHydrated
  canCreateMutation(): { allowed: boolean; error?: string } {
    if (!this.state.isHydrated) {
      return { allowed: false, error: 'Cannot create mutation before hydration' };
    }
    if (this.pendingMutations.size >= PENDING_MUTATIONS_HARD_LIMIT) {
      libraryLog.alert({
        type: 'pending_overflow',
        details: { count: this.pendingMutations.size },
      });
      return { allowed: false, error: 'Too many actions pending. Please wait.' };
    }
    return { allowed: true };
  }

  // Create a pending mutation with snapshot
  // INV-16: isPlaceholder/isTombstone for unhydrated entity mutations
  createMutation(
    key: MutationKey,
    action: MutationAction,
    snapshot: StateSnapshot,
    options?: { isPlaceholder?: boolean; isTombstone?: boolean }
  ): PendingMutation | null {
    const check = this.canCreateMutation();
    if (!check.allowed) {
      console.error(check.error);
      return null;
    }

    // INV-1: At most ONE pending mutation per key - supersede previous
    const existing = this.pendingMutations.get(key);
    if (existing) {
      existing.abortController.abort();
      libraryLog.mutationSuperseded({
        key,
        oldMutationId: existing.id,
        newMutationId: '', // Will be set below
      });
      this.pendingMutations.delete(key);
      // Clear timeout for old mutation
      const timeoutId = this.timeoutIds.get(existing.id);
      if (timeoutId) {
        clearTimeout(timeoutId);
        this.timeoutIds.delete(existing.id);
      }
    }

    const mutation: PendingMutation = {
      id: generateMutationId(),
      key,
      action,
      createdAt: Date.now(),
      snapshot,
      abortController: new AbortController(),
      isPlaceholder: options?.isPlaceholder,
      isTombstone: options?.isTombstone,
    };

    // Update the superseded log with correct new ID
    if (existing) {
      libraryLog.mutationSuperseded({
        key,
        oldMutationId: existing.id,
        newMutationId: mutation.id,
      });
    }

    this.pendingMutations.set(key, mutation);

    // Track last mutation time for this key (INV-3)
    this.state.lastMutationTimeForKey.set(key, mutation.createdAt);

    libraryLog.mutationCreated({
      key,
      mutationId: mutation.id,
      action,
      timestamp: mutation.createdAt,
    });

    // INV-5: Set timeout for auto-resolution
    const timeoutId = setTimeout(() => {
      const current = this.pendingMutations.get(key);
      if (current && current.id === mutation.id) {
        libraryLog.mutationStaleExpired({
          key,
          mutationId: mutation.id,
          ageMs: Date.now() - mutation.createdAt,
        });
        // Mutation will be resolved via sync
      }
    }, MUTATION_TIMEOUT_MS);
    this.timeoutIds.set(mutation.id, timeoutId);

    this.notify();
    return mutation;
  }

  // Confirm mutation success
  confirmMutation(key: MutationKey, mutationId: string, latencyMs: number, serverDuplicate?: boolean) {
    const mutation = this.pendingMutations.get(key);

    // INV-11: Ignore superseded mutation's response
    if (!mutation || mutation.id !== mutationId) {
      return;
    }

    libraryLog.mutationConfirmed({
      key,
      mutationId,
      latencyMs,
      serverDuplicate,
    });

    // Clear mutation
    this.pendingMutations.delete(key);
    const timeoutId = this.timeoutIds.get(mutationId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.timeoutIds.delete(mutationId);
    }

    this.notify();
  }

  // Rollback mutation on failure
  rollbackMutation(key: MutationKey, mutationId: string, error: string, statusCode?: number) {
    const mutation = this.pendingMutations.get(key);

    // INV-11: Ignore superseded mutation's response
    if (!mutation || mutation.id !== mutationId) {
      return;
    }

    libraryLog.mutationFailed({
      key,
      mutationId,
      error,
      statusCode,
      willRollback: true,
    });

    // INV-10: Restore EXACT snapshot state
    const snapshot = mutation.snapshot;
    const parts = key.split(':');

    if (parts[0] === 'list') {
      // Custom list mutation: list:listId:mediaType:mediaId
      const [, listId, mediaType, mediaIdStr] = parts;
      const itemKey = getCustomListItemKey(listId, parseInt(mediaIdStr), mediaType as MediaType);
      const newCustomListItems = new Map(this.state.customListItems);

      if (snapshot.inList === true) {
        // Was in the list, restore it
        newCustomListItems.set(itemKey, {
          mediaId: parseInt(mediaIdStr),
          mediaType: mediaType as MediaType,
        });
      } else {
        // Was not in the list (or was placeholder), remove it
        newCustomListItems.delete(itemKey);
      }
      // Create new state object to trigger React re-render
      this.state = { ...this.state, customListItems: newCustomListItems };
    } else {
      // Default list or rating mutation: category:mediaType:mediaId
      const [category, mediaType, mediaIdStr] = parts;
      const mediaKey = `${mediaType}:${mediaIdStr}`;

      if (category === 'watched' || category === 'watchlist' || category === 'favorites') {
        const newMap = new Map(this.state[category as 'watched' | 'watchlist' | 'favorites']);
        if (snapshot[category as keyof StateSnapshot] === true) {
          // Was in the list, restore it
          newMap.set(mediaKey, {
            mediaId: parseInt(mediaIdStr),
            mediaType: mediaType as MediaType,
          });
        } else {
          // Was not in the list, remove it
          newMap.delete(mediaKey);
        }
        // Create new state object to trigger React re-render
        this.state = { ...this.state, [category]: newMap };
      } else if (category === 'rating') {
        const newRatings = new Map(this.state.ratings);
        if (snapshot.rating !== null && snapshot.rating !== undefined) {
          newRatings.set(mediaKey, snapshot.rating);
        } else {
          newRatings.delete(mediaKey);
        }
        // Create new state object to trigger React re-render
        this.state = { ...this.state, ratings: newRatings };
      }
    }

    libraryLog.mutationRolledback({
      key,
      mutationId,
      snapshotAgeMs: Date.now() - snapshot.capturedAt,
    });

    // Clear mutation
    this.pendingMutations.delete(key);
    const timeoutId = this.timeoutIds.get(mutationId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.timeoutIds.delete(mutationId);
    }

    this.notify();
  }

  // Optimistic update for watched/watchlist/favorites
  optimisticUpdate(
    category: 'watched' | 'watchlist' | 'favorites',
    mediaId: number,
    mediaType: MediaType,
    add: boolean,
    itemId?: string
  ) {
    const key = getMediaKey(mediaId, mediaType);
    const newMap = new Map(this.state[category]);

    if (add) {
      newMap.set(key, { mediaId, mediaType, itemId });
    } else {
      newMap.delete(key);
    }

    // Create new state object to trigger React re-render
    this.state = { ...this.state, [category]: newMap };
    this.notify();
  }

  // Optimistic update for rating
  optimisticRatingUpdate(mediaId: number, mediaType: MediaType, rating: number | null) {
    const key = getMediaKey(mediaId, mediaType);
    const newRatings = new Map(this.state.ratings);

    if (rating !== null) {
      newRatings.set(key, rating);
    } else {
      newRatings.delete(key);
    }

    // Create new state object to trigger React re-render
    this.state = { ...this.state, ratings: newRatings };
    this.notify();
  }

  // Update item ID after server confirms
  updateItemId(
    category: 'watched' | 'watchlist' | 'favorites',
    mediaId: number,
    mediaType: MediaType,
    itemId: string
  ) {
    const key = getMediaKey(mediaId, mediaType);
    const currentMap = this.state[category];
    const item = currentMap.get(key);
    if (item) {
      const newMap = new Map(currentMap);
      newMap.set(key, { ...item, itemId });
      // Create new state object to trigger React re-render
      this.state = { ...this.state, [category]: newMap };
      this.notify();
    }
  }

  // Custom list optimistic update
  optimisticCustomListUpdate(
    listId: string,
    mediaId: number,
    mediaType: MediaType,
    add: boolean,
    itemId?: string,
    isPlaceholder?: boolean
  ) {
    const key = getCustomListItemKey(listId, mediaId, mediaType);
    const newCustomListItems = new Map(this.state.customListItems);

    if (add) {
      newCustomListItems.set(key, {
        mediaId,
        mediaType,
        itemId,
        _placeholder: isPlaceholder,
      });
    } else {
      newCustomListItems.delete(key);
    }

    // Create new state object to trigger React re-render
    this.state = { ...this.state, customListItems: newCustomListItems };
    this.notify();
  }

  // Optimistic update for following a user
  optimisticFollowUpdate(targetUserId: string, follow: boolean) {
    const newFollowing = new Set(this.state.followingUsers);
    if (follow) {
      newFollowing.add(targetUserId);
    } else {
      newFollowing.delete(targetUserId);
    }
    this.state = { ...this.state, followingUsers: newFollowing };
    this.notify();
  }

  // Rollback follow mutation
  rollbackFollowUpdate(targetUserId: string, wasFollowing: boolean) {
    const newFollowing = new Set(this.state.followingUsers);
    if (wasFollowing) {
      newFollowing.add(targetUserId);
    } else {
      newFollowing.delete(targetUserId);
    }
    this.state = { ...this.state, followingUsers: newFollowing };
    this.notify();
  }

  // Check if following a user
  isFollowingUser(userId: string): boolean {
    return this.state.followingUsers.has(userId);
  }

  // Set following users (for hydration)
  setFollowingUsers(userIds: string[]) {
    this.state = { ...this.state, followingUsers: new Set(userIds) };
    this.notify();
  }

  // Update custom list item ID after server confirms
  updateCustomListItemId(
    listId: string,
    mediaId: number,
    mediaType: MediaType,
    itemId: string
  ) {
    const key = getCustomListItemKey(listId, mediaId, mediaType);
    const item = this.state.customListItems.get(key);
    if (item) {
      const newCustomListItems = new Map(this.state.customListItems);
      newCustomListItems.set(key, { ...item, itemId, _placeholder: false });
      // Create new state object to trigger React re-render
      this.state = { ...this.state, customListItems: newCustomListItems };
      this.notify();
    }
  }

  // Check if item is in custom list
  isInCustomList(listId: string, mediaId: number, mediaType: MediaType): boolean {
    const key = getCustomListItemKey(listId, mediaId, mediaType);
    const item = this.state.customListItems.get(key);
    // Don't count tombstones as "in list"
    return item ? !item._tombstone : false;
  }

  // Get custom list item ID
  getCustomListItemId(listId: string, mediaId: number, mediaType: MediaType): string | undefined {
    const key = getCustomListItemKey(listId, mediaId, mediaType);
    return this.state.customListItems.get(key)?.itemId;
  }

  // Get all custom lists metadata
  getCustomLists(): CustomListMeta[] {
    return Array.from(this.state.customLists.values());
  }

  // Set custom list items (after on-demand fetch)
  setCustomListItems(listId: string, items: LibraryItem[]) {
    // Preserve items with pending mutations
    const pendingKeys = new Set<string>();
    for (const [mutKey] of this.pendingMutations) {
      if (mutKey.startsWith(`list:${listId}:`)) {
        const parts = mutKey.split(':');
        if (parts.length >= 4) {
          pendingKeys.add(`${parts[1]}:${parts[2]}:${parts[3]}`);
        }
      }
    }

    const newCustomListItems = new Map(this.state.customListItems);

    // Remove existing items for this list (except pending)
    for (const [key] of this.state.customListItems) {
      if (key.startsWith(`${listId}:`) && !pendingKeys.has(key)) {
        newCustomListItems.delete(key);
      }
    }

    // Add new items
    for (const item of items) {
      const key = getCustomListItemKey(listId, item.mediaId, item.mediaType);
      if (!pendingKeys.has(key)) {
        newCustomListItems.set(key, item);
      }
    }

    // Create new state object to trigger React re-render
    this.state = { ...this.state, customListItems: newCustomListItems };
    this.notify();
  }

  hasPendingMutation(key: MutationKey): boolean {
    return this.pendingMutations.has(key);
  }

  getPendingMutation(key: MutationKey): PendingMutation | undefined {
    return this.pendingMutations.get(key);
  }

  get pendingMutationCount(): number {
    return this.pendingMutations.size;
  }

  // SNAPSHOT_RETENTION_MS: Clean up expired snapshots (older than 60s)
  cleanupExpiredSnapshots() {
    const now = Date.now();
    for (const [key, mutation] of this.pendingMutations) {
      if (now - mutation.snapshot.capturedAt > SNAPSHOT_RETENTION_MS) {
        // Snapshot expired - abort and remove mutation
        mutation.abortController.abort();
        libraryLog.mutationDropped({
          key,
          mutationId: mutation.id,
          reason: 'snapshot_expired',
        });
        this.pendingMutations.delete(key);
        const timeoutId = this.timeoutIds.get(mutation.id);
        if (timeoutId) {
          clearTimeout(timeoutId);
          this.timeoutIds.delete(mutation.id);
        }
      }
    }
  }

  // MAX_DIVERGENCE_MS: Check if any mutations are too old and need hard sync
  checkDivergence(): boolean {
    const now = Date.now();
    for (const mutation of this.pendingMutations.values()) {
      if (now - mutation.createdAt > MAX_DIVERGENCE_MS) {
        libraryLog.syncHardRehydrate({ reason: 'max_divergence', mutationAgeMs: now - mutation.createdAt });
        return true; // Need hard rehydrate
      }
    }
    return false;
  }

  startSyncInterval() {
    if (this.syncIntervalId) return;
    this.syncIntervalId = setInterval(() => {
      // Cleanup expired snapshots
      this.cleanupExpiredSnapshots();
      // Check for MAX_DIVERGENCE
      if (this.checkDivergence()) {
        // Hard rehydrate needed - notify component
        this.notify();
        return;
      }
      libraryLog.syncStarted({ trigger: 'interval' });
      // Sync will be triggered by component
    }, SYNC_INTERVAL_MS);
  }

  stopSyncInterval() {
    if (this.syncIntervalId) {
      clearInterval(this.syncIntervalId);
      this.syncIntervalId = null;
    }
  }

  getInactiveTime(): number {
    return Date.now() - this.lastFocusTime;
  }

  destroy() {
    this.stopSyncInterval();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnline);
    }
    this.broadcastChannel?.close();
    for (const timeoutId of this.timeoutIds.values()) {
      clearTimeout(timeoutId);
    }
  }
}

// Singleton store instance
const libraryStore = new LibraryStore();

// =============================================================================
// CONTEXT
// =============================================================================

const UserLibraryContext = createContext<UserLibraryContextValue | null>(null);

interface UserLibraryProviderProps {
  children: ReactNode;
}

export function UserLibraryProvider({ children }: UserLibraryProviderProps) {
  const { user, session } = useAuth();
  const queryClient = useQueryClient();
  const { isInList, fetchListItems, toggleListItem } = useLists();
  const state = useSyncExternalStore(
    libraryStore.subscribe,
    libraryStore.getSnapshot,
    libraryStore.getSnapshot
  );

  // Phase 4C: Helper to invalidate React Query cache after default list mutations
  const invalidateDefaultListCache = useCallback(async (slug: 'watchlist' | 'watched' | 'favorites') => {
    if (!user?.id) return;

    // Invalidate mine query immediately for sidebar item_count updates
    await queryClient.invalidateQueries({ queryKey: queryKeys.lists.mine(user.id) });

    // Delay items + detail invalidation by 3s to avoid racing with in-flight
    // reorder operations. The optimistic cache is correct for the mutation that
    // just completed; this delayed refetch ensures convergence for collaborative
    // edits and multi-device usage without causing visible snap-back.
    const userId = user.id;
    setTimeout(async () => {
      const lists = queryClient.getQueryData<WatchlistMeta[]>(
        queryKeys.lists.mine(userId)
      );
      const defaultList = lists?.find((l: WatchlistMeta) => l.slug === slug && l.is_default);

      if (defaultList) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.lists.detail(defaultList.id) });
        invalidateAllListItemsQueries(queryClient, defaultList.id);
      }
    }, 3000);
  }, [user?.id, queryClient]);

  // Phase 4D: Helper to OPTIMISTICALLY patch default list items cache (Spotify instant UX)
  // This makes items appear immediately on WatchlistPage instead of waiting for refetch
  // TASK A: Now accepts enrichment fields for full metadata display on WatchlistPage
  const optimisticPatchDefaultListCache = useCallback((
    slug: 'watchlist' | 'watched' | 'favorites',
    mediaId: number,
    mediaType: MediaType,
    isAdding: boolean,
    mediaTitle: string,
    mediaPoster?: string,
    mediaYear?: number,
    enrichment?: {
      genre_ids?: number[];
      runtime?: number | null;
      imdb_rating?: number;
      rt_rating?: number;
      filmpl_rating?: number;
      popularity?: number;
      original_language?: string;
      vote_count?: number;
    }
  ) => {
    if (!user?.id) return;

    // Find the default list ID from React Query cache first
    const lists = queryClient.getQueryData<WatchlistMeta[]>(
      queryKeys.lists.mine(user.id)
    );
    const defaultListFromCache = lists?.find((l) => l.slug === slug && l.is_default);

    // Fallback to LibraryStore's defaultListIds if mine cache is missing (cold start)
    const listId = defaultListFromCache?.id ?? state.defaultListIds?.[slug];
    if (!listId) return;

    // Cancel in-flight queries synchronously (fire-and-forget)
    void cancelAllListItemsQueries(queryClient, listId).catch(() => {});

    // Optimistically patch the list items cache (variant-aware placement)
    setAllListItemsDataWithSortInfo(queryClient, listId, (old, sortParams) => {
      if (!old) {
        // If no cache exists, create a minimal one for the optimistic item
        if (!isAdding) return old; // Can't remove from empty cache
        const optimisticItem: WatchlistItemRow = {
          id: `temp-${Date.now()}`,
          watchlist_id: listId,
          media_id: mediaId,
          media_type: mediaType,
          media_title: mediaTitle,
          media_poster: mediaPoster ?? null,
          media_year: mediaYear ?? null,
          notes: null,
          position: 999999,
          added_at: new Date().toISOString(),
          genre_ids: enrichment?.genre_ids,
          runtime: enrichment?.runtime,
          imdb_rating: enrichment?.imdb_rating,
          rt_rating: enrichment?.rt_rating,
          filmpl_rating: enrichment?.filmpl_rating,
          popularity: enrichment?.popularity,
          original_language: enrichment?.original_language,
          vote_count: enrichment?.vote_count,
        };
        // Create minimal cache without watchlist meta (we may not have it)
        return {
          pages: [{
            items: [optimisticItem],
            pagination: { page: 1, limit: 100, total: 1, has_more: false },
            watchlist: defaultListFromCache ?? null,
          }],
          pageParams: [1],
        };
      }

      if (isAdding) {
        // Variant-aware + last-page-aware placement
        const optimisticItem: WatchlistItemRow = {
          id: `temp-${Date.now()}`,
          watchlist_id: listId,
          media_id: mediaId,
          media_type: mediaType,
          media_title: mediaTitle,
          media_poster: mediaPoster ?? null,
          media_year: mediaYear ?? null,
          notes: null,
          position: 999999,
          added_at: new Date().toISOString(),
          genre_ids: enrichment?.genre_ids,
          runtime: enrichment?.runtime,
          imdb_rating: enrichment?.imdb_rating,
          rt_rating: enrichment?.rt_rating,
          filmpl_rating: enrichment?.filmpl_rating,
          popularity: enrichment?.popularity,
          original_language: enrichment?.original_language,
          vote_count: enrichment?.vote_count,
        };
        const shouldAppend =
          sortParams.sortBy === 'position' ||
          (sortParams.sortBy === 'added_at' && sortParams.sortOrder === 'asc');

        const lastIdx = old.pages.length - 1;
        const lastPageFullyLoaded = !old.pages[lastIdx]?.pagination.has_more;

        const newPages = old.pages.map((page, idx) => {
          let newItems = page.items;

          if (shouldAppend) {
            // Append: insert on last page ONLY if fully loaded
            if (idx === lastIdx && lastPageFullyLoaded) {
              newItems = [...page.items, optimisticItem];
            }
          } else {
            // Prepend: insert on first page
            if (idx === 0) {
              newItems = [optimisticItem, ...page.items];
            }
          }

          // Update item_count on page 0's watchlist meta
          const updatedWatchlist = idx === 0 && page.watchlist
            ? { ...page.watchlist, item_count: (page.watchlist.item_count || 0) + 1 }
            : page.watchlist;

          return { ...page, items: newItems, watchlist: updatedWatchlist };
        });
        return { ...old, pages: newPages };
      } else {
        // Remove item
        const newPages = old.pages.map((page, idx) => {
          const filtered = page.items.filter(
            (i) => !(i.media_id === mediaId && i.media_type === mediaType)
          );
          const removed = filtered.length < page.items.length;
          return {
            ...page,
            items: filtered,
            watchlist:
              removed && page.watchlist && idx === 0
                ? { ...page.watchlist, item_count: Math.max(0, (page.watchlist.item_count || 0) - 1) }
                : page.watchlist,
          };
        });
        return { ...old, pages: newPages };
      }
    });

    // Also update the mine query's item_count optimistically (only if mine cache exists)
    if (lists) {
      queryClient.setQueryData<WatchlistMeta[]>(
        queryKeys.lists.mine(user.id),
        (old) => {
          if (!old) return old;
          return old.map((list) => {
            if (list.id === listId) {
              const delta = isAdding ? 1 : -1;
              return { ...list, item_count: Math.max(0, (list.item_count || 0) + delta) };
            }
            return list;
          });
        }
      );
    }
  }, [user?.id, queryClient, state.defaultListIds]);

  // Track if we've done initial hydration for this user
  const hydratedUserIdRef = useRef<string | null>(null);

  // Track previous user ID for cache cleanup on logout
  const prevUserIdRef = useRef<string | null>(null);

  // Track pending localStorage persist timeout (for flush-on-exit)
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Restore cached library from localStorage on login (before network hydration)
  useEffect(() => {
    if (!user?.id) return;

    // Skip if already hydrated for this user
    if (state.isHydrated && state.userId === user.id) return;

    try {
      const cached = localStorage.getItem(getLibraryCacheKey(user.id));
      if (!cached) return;

      const data = JSON.parse(cached);

      // Minimal validation
      if (
        !Array.isArray(data.watched) ||
        !Array.isArray(data.watchlist) ||
        !Array.isArray(data.favorites) ||
        !Array.isArray(data.customLists) ||
        typeof data.ratings !== 'object'
      ) {
        return;
      }

      libraryStore.hydrateFromCache(data);
    } catch {
      // Invalid cache - ignore
    }
  }, [user?.id, state.isHydrated, state.userId]);

  // Hydrate library from server
  const hydrate = useCallback(async () => {
    if (!session?.access_token || !user) return;

    libraryLog.hydrationStarted({ userId: user.id });
    const startTime = Date.now();

    try {
      // Fetch library data and following IDs in parallel
      const [libraryResponse, followingResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/user/library`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        }),
        fetch(`${API_BASE_URL}/user/following-ids`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        }).catch(() => null), // Non-fatal if following endpoint fails
      ]);

      if (!libraryResponse.ok) {
        if (libraryResponse.status === 401) {
          libraryLog.authTokenExpired();
          libraryStore.clear();
          return;
        }
        throw new Error(`HTTP ${libraryResponse.status}`);
      }

      const data = await libraryResponse.json();

      // Parse following IDs (non-fatal if fails)
      let followingIds: string[] = [];
      if (followingResponse?.ok) {
        try {
          const followingData = await followingResponse.json();
          followingIds = followingData.following_ids || [];
        } catch {
          // Ignore parse errors
        }
      }

      libraryStore.hydrate({
        watched: data.watched || [],
        watchlist: data.watchlist || [],
        favorites: data.favorites || [],
        ratings: data.ratings || {},
        customLists: data.customLists || [],
        defaultListIds: data.defaultListIds ?? null,
        syncedAt: data.syncedAt || Date.now(),
      });

      // Set following users separately (hydrate doesn't handle it)
      if (followingIds.length > 0) {
        libraryStore.setFollowingUsers(followingIds);
      }

      libraryLog.hydrationCompleted({
        itemCounts: {
          watched: data.watched?.length || 0,
          watchlist: data.watchlist?.length || 0,
          favorites: data.favorites?.length || 0,
          ratings: Object.keys(data.ratings || {}).length,
          lists: data.customLists?.length || 0,
        },
        durationMs: Date.now() - startTime,
        paginated: false,
      });

      hydratedUserIdRef.current = user.id;
    } catch (err) {
      libraryLog.hydrationFailed({
        error: err instanceof Error ? err.message : 'Unknown error',
        statusCode: undefined,
      });
    }
  }, [session?.access_token, user]);

  // Soft sync
  const softSync = useCallback(async () => {
    if (!session?.access_token || !user || !state.isHydrated) return;

    libraryLog.syncStarted({ trigger: 'manual' });
    const startTime = Date.now();

    try {
      const response = await fetch(`${API_BASE_URL}/user/library`, {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      libraryStore.softSync({
        watched: data.watched || [],
        watchlist: data.watchlist || [],
        favorites: data.favorites || [],
        ratings: data.ratings || {},
        customLists: data.customLists || [],
        defaultListIds: data.defaultListIds ?? null,
        syncedAt: data.syncedAt || Date.now(),
      });

      libraryLog.syncCompleted({
        added: 0, // TODO: Calculate
        removed: 0,
        protected: libraryStore.pendingMutationCount,
        durationMs: Date.now() - startTime,
      });
    } catch (err) {
      libraryLog.syncFailed({
        error: err instanceof Error ? err.message : 'Unknown error',
        willRetry: true,
      });
    }
  }, [session?.access_token, user, state.isHydrated]);

  // Hard rehydrate (clear + fetch fresh)
  const hardRehydrate = useCallback(async () => {
    libraryLog.syncHardRehydrate({ reason: 'max_divergence' });
    libraryStore.setHydrated(false);
    await hydrate();
  }, [hydrate]);

  // Sync user ID and handle user changes
  useEffect(() => {
    libraryStore.setUserId(user?.id ?? null);
  }, [user?.id]);

  // Initial hydration when user logs in
  useEffect(() => {
    if (user && session?.access_token && hydratedUserIdRef.current !== user.id) {
      hydrate();
    }
  }, [user, session?.access_token, hydrate]);

  // Start sync interval when hydrated
  useEffect(() => {
    if (state.isHydrated) {
      libraryStore.startSyncInterval();
    } else {
      libraryStore.stopSyncInterval();
    }
  }, [state.isHydrated]);

  // Helper to build cache payload (used by both debounced persist and flush-on-exit)
  const buildLibraryCacheData = useCallback(() => {
    return {
      defaultListIds: state.defaultListIds,
      watched: Array.from(state.watched.values()).map(({ mediaId, mediaType, itemId }) => ({
        mediaId,
        mediaType,
        itemId,
      })),
      watchlist: Array.from(state.watchlist.values()).map(({ mediaId, mediaType, itemId }) => ({
        mediaId,
        mediaType,
        itemId,
      })),
      favorites: Array.from(state.favorites.values()).map(({ mediaId, mediaType, itemId }) => ({
        mediaId,
        mediaType,
        itemId,
      })),
      ratings: Object.fromEntries(state.ratings),
      customLists: Array.from(state.customLists.values()).map((list) => ({
        id: list.id,
        name: list.name,
        slug: list.slug,
        item_count: list.item_count,
        is_default: list.is_default,
        nextCursor: list.nextCursor ?? null,
      })),
      customListItems: Array.from(state.customListItems.entries()).map(([key, item]) => ({
        key,
        mediaId: item.mediaId,
        mediaType: item.mediaType,
        itemId: item.itemId,
      })),
      followingUsers: Array.from(state.followingUsers),
      syncedAt: state.lastSyncedAt ?? Date.now(),
    };
  }, [
    state.defaultListIds,
    state.watched,
    state.watchlist,
    state.favorites,
    state.ratings,
    state.customLists,
    state.customListItems,
    state.followingUsers,
    state.lastSyncedAt,
  ]);

  // Persist library to localStorage (debounced)
  useEffect(() => {
    if (!state.userId || !state.isHydrated) return;

    // Clear any pending persist timer before scheduling a new one
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
      persistTimerRef.current = null;
    }

    persistTimerRef.current = setTimeout(() => {
      try {
        const cacheData = buildLibraryCacheData();
        localStorage.setItem(getLibraryCacheKey(state.userId!), JSON.stringify(cacheData));
      } catch {
        // localStorage quota exceeded or unavailable - ignore
      }
      persistTimerRef.current = null;
    }, LIBRARY_PERSIST_DEBOUNCE_MS);

    return () => {
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
      }
    };
  }, [
    state.userId,
    state.isHydrated,
    buildLibraryCacheData,
  ]);

  // Flush localStorage immediately on page exit (pagehide + visibility hidden)
  // This ensures library state is persisted before refresh/navigation
  useEffect(() => {
    if (!state.userId || !state.isHydrated) return;

    const flushToLocalStorage = () => {
      // Clear pending debounce to avoid double-write with stale data
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
      }

      try {
        const cacheData = buildLibraryCacheData();
        localStorage.setItem(getLibraryCacheKey(state.userId!), JSON.stringify(cacheData));
      } catch {
        // localStorage quota exceeded or unavailable - ignore
      }
    };

    const handlePageHide = () => {
      flushToLocalStorage();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushToLocalStorage();
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [state.userId, state.isHydrated, buildLibraryCacheData]);

  // Clean up cache on logout (track previous user)
  useEffect(() => {
    if (prevUserIdRef.current && !user?.id) {
      try {
        localStorage.removeItem(getLibraryCacheKey(prevUserIdRef.current));
      } catch {
        // Ignore
      }
    }
    prevUserIdRef.current = user?.id ?? null;
  }, [user?.id]);

  // Status check functions (synchronous)
  const isWatched = useCallback(
    (mediaId: number, mediaType: MediaType) => {
      return state.watched.has(getMediaKey(mediaId, mediaType));
    },
    [state.watched]
  );

  const isInWatchlist = useCallback(
    (mediaId: number, mediaType: MediaType) => {
      return state.watchlist.has(getMediaKey(mediaId, mediaType));
    },
    [state.watchlist]
  );

  const isInFavorites = useCallback(
    (mediaId: number, mediaType: MediaType) => {
      return state.favorites.has(getMediaKey(mediaId, mediaType));
    },
    [state.favorites]
  );

  const getRating = useCallback(
    (mediaId: number, mediaType: MediaType) => {
      return state.ratings.get(getMediaKey(mediaId, mediaType)) ?? null;
    },
    [state.ratings]
  );

  const getItemId = useCallback(
    (category: 'watched' | 'watchlist' | 'favorites', mediaId: number, mediaType: MediaType) => {
      const snap = libraryStore.getSnapshot();
      return snap[category].get(getMediaKey(mediaId, mediaType))?.itemId;
    },
    []
  );

  // Custom list status checks
  const isInCustomList = useCallback(
    (listId: string, mediaId: number, mediaType: MediaType) => {
      return isInList(listId, mediaId, mediaType);
    },
    [isInList]
  );

  const getCustomLists = useCallback(() => {
    return libraryStore.getCustomLists();
  }, [state.customLists]);

  const getCustomListItemId = useCallback(
    (listId: string, mediaId: number, mediaType: MediaType) => {
      return libraryStore.getCustomListItemId(listId, mediaId, mediaType);
    },
    [state.customListItems]
  );

  // Fetch custom list items on demand
  // Includes in-flight dedupe and permanent failure latch
  const fetchCustomListItems = useCallback(
    async (listId: string): Promise<{ status: 'success' | 'error' | 'skipped' }> => {
      try {
        if (!user) return { status: 'skipped' };
        await fetchListItems(listId, { reset: true });
        return { status: 'success' };
      } catch (err) {
        console.error('Failed to fetch custom list items:', err);
        return { status: 'error' };
      }
    },
    [user, fetchListItems]
  );

  // Toggle custom list membership (optimistic with INV-16)
  // TASK B: Now accepts knownItemId and forceRemove for deterministic removal
  const toggleCustomList = useCallback(
    async (listId: string, mediaId: number, mediaType: MediaType, mediaTitle: string, mediaPoster?: string, mediaYear?: number, knownItemId?: string, forceRemove?: boolean) => {
      if (!user) return null;
      return await toggleListItem(listId, mediaId, mediaType, mediaTitle, mediaPoster, mediaYear, knownItemId, forceRemove);
    },
    [user, toggleListItem]
  );

  // Toggle watched status (optimistic)
  // TASK A: Now accepts enrichment for instant metadata display
  const toggleWatched = useCallback(
    async (mediaId: number, mediaType: MediaType, mediaTitle: string, mediaPoster?: string, mediaYear?: number, enrichment?: MediaEnrichment) => {
      if (!session?.access_token || !user) return null;

      const key = getMediaKey(mediaId, mediaType);
      const mutationKey = getMutationKey('watched', mediaId, mediaType);
      const isCurrentlyWatched = state.watched.has(key);
      const currentItem = state.watched.get(key);
      const desiredInList = !isCurrentlyWatched;

      // OUTBOX: Persist intent BEFORE mutation (survives refresh/navigation)
      // CRITICAL: Use slug as listId for stable keying (last-intent-wins)
      enqueueIntent(user.id, {
        listId: 'watched', // Stable key - NOT UUID (would break last-intent-wins)
        listSlug: 'watched',
        isDefaultList: true,
        mediaId,
        mediaType,
        mediaTitle,
        mediaPoster,
        mediaYear,
        desiredInList,
        itemId: currentItem?.itemId,
      });

      // INV-9: Capture snapshot BEFORE optimistic update
      const snapshot: StateSnapshot = {
        capturedAt: Date.now(),
        mutationKey,
        watched: isCurrentlyWatched,
      };

      const mutation = libraryStore.createMutation(mutationKey, isCurrentlyWatched ? 'remove' : 'add', snapshot);
      if (!mutation) return null;

      // Optimistic update
      libraryStore.optimisticUpdate('watched', mediaId, mediaType, !isCurrentlyWatched);

      // Patch React Query cache for instant WatchlistPage appearance
      // TASK A: Pass enrichment for full metadata display
      optimisticPatchDefaultListCache('watched', mediaId, mediaType, !isCurrentlyWatched, mediaTitle, mediaPoster, mediaYear, enrichment);

      const startTime = Date.now();

      try {
        let response: Response;

        if (isCurrentlyWatched && currentItem?.itemId) {
          // Remove from watched - use itemId-only endpoint (no watchlistId needed)
          response = await fetch(`${API_BASE_URL}/watchlist-items/${currentItem.itemId}`, {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              'Idempotency-Key': mutation.id,
            },
            signal: mutation.abortController.signal,
          });
        } else {
          // Add to watched
          response = await fetch(`${API_BASE_URL}/watchlists/quick-add`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.access_token}`,
              'Idempotency-Key': mutation.id,
            },
            body: JSON.stringify({
              slug: 'watched',
              media_id: mediaId,
              media_type: mediaType,
              media_title: mediaTitle,
              media_poster: mediaPoster,
              media_year: mediaYear,
            }),
            signal: mutation.abortController.signal,
          });
        }

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || `HTTP ${response.status}`);
        }

        const data = await response.json();

        // Update item ID if we added
        if (!isCurrentlyWatched && data.item?.id) {
          libraryStore.updateItemId('watched', mediaId, mediaType, data.item.id);
          // Patch React Query list-items cache: replace temp ID with real server ID
          const watchedList = queryClient.getQueryData<WatchlistMeta[]>(
            queryKeys.lists.mine(user.id)
          )?.find((l: WatchlistMeta) => l.slug === 'watched' && l.is_default);
          if (watchedList) {
            setAllListItemsDataWithSortInfo(queryClient, watchedList.id, (old) => {
              if (!old) return old;
              return {
                ...old,
                pages: old.pages.map((page) => ({
                  ...page,
                  items: page.items.map((item) =>
                    item.media_id === mediaId && item.media_type === mediaType && item.id.startsWith('temp-')
                      ? { ...item, id: data.item.id }
                      : item
                  ),
                })),
              };
            });
          }
        }

        libraryStore.confirmMutation(mutationKey, mutation.id, Date.now() - startTime, data.duplicate);

        // Phase 4B: Invalidate React Query cache after successful mutation
        invalidateDefaultListCache('watched');

        return mutation.id;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          // Mutation was superseded - don't rollback, return null (callers ignore)
          return null;
        }
        // Rollback and throw - callers must handle with try/catch
        libraryStore.rollbackMutation(
          mutationKey,
          mutation.id,
          err instanceof Error ? err.message : 'Unknown error'
        );
        throw err;
      }
    },
    [session?.access_token, user, state.watched, invalidateDefaultListCache, optimisticPatchDefaultListCache]
  );

  // Toggle watchlist status (optimistic)
  // TASK A: Now accepts enrichment for instant metadata display
  const toggleWatchlist = useCallback(
    async (mediaId: number, mediaType: MediaType, mediaTitle: string, mediaPoster?: string, mediaYear?: number, enrichment?: MediaEnrichment) => {
      if (!session?.access_token || !user) return null;

      const key = getMediaKey(mediaId, mediaType);
      const mutationKey = getMutationKey('watchlist', mediaId, mediaType);
      const isCurrentlyInList = state.watchlist.has(key);
      const currentItem = state.watchlist.get(key);
      const desiredInList = !isCurrentlyInList;

      // OUTBOX: Persist intent BEFORE mutation (survives refresh/navigation)
      // CRITICAL: Use slug as listId for stable keying (last-intent-wins)
      enqueueIntent(user.id, {
        listId: 'watchlist', // Stable key - NOT UUID (would break last-intent-wins)
        listSlug: 'watchlist',
        isDefaultList: true,
        mediaId,
        mediaType,
        mediaTitle,
        mediaPoster,
        mediaYear,
        desiredInList,
        itemId: currentItem?.itemId,
      });

      const snapshot: StateSnapshot = {
        capturedAt: Date.now(),
        mutationKey,
        watchlist: isCurrentlyInList,
      };

      const mutation = libraryStore.createMutation(mutationKey, isCurrentlyInList ? 'remove' : 'add', snapshot);
      if (!mutation) return null;

      libraryStore.optimisticUpdate('watchlist', mediaId, mediaType, !isCurrentlyInList);

      // Patch React Query cache for instant WatchlistPage appearance
      // TASK A: Pass enrichment for full metadata display
      optimisticPatchDefaultListCache('watchlist', mediaId, mediaType, !isCurrentlyInList, mediaTitle, mediaPoster, mediaYear, enrichment);

      const startTime = Date.now();

      try {
        let response: Response;

        if (isCurrentlyInList && currentItem?.itemId) {
          // Remove from watchlist - use itemId-only endpoint (no watchlistId needed)
          response = await fetch(`${API_BASE_URL}/watchlist-items/${currentItem.itemId}`, {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              'Idempotency-Key': mutation.id,
            },
            signal: mutation.abortController.signal,
          });
        } else {
          response = await fetch(`${API_BASE_URL}/watchlists/quick-add`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.access_token}`,
              'Idempotency-Key': mutation.id,
            },
            body: JSON.stringify({
              slug: 'watchlist',
              media_id: mediaId,
              media_type: mediaType,
              media_title: mediaTitle,
              media_poster: mediaPoster,
              media_year: mediaYear,
            }),
            signal: mutation.abortController.signal,
          });
        }

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || `HTTP ${response.status}`);
        }

        const data = await response.json();

        if (!isCurrentlyInList && data.item?.id) {
          libraryStore.updateItemId('watchlist', mediaId, mediaType, data.item.id);
          // Patch React Query list-items cache: replace temp ID with real server ID
          const watchlistList = queryClient.getQueryData<WatchlistMeta[]>(
            queryKeys.lists.mine(user.id)
          )?.find((l: WatchlistMeta) => l.slug === 'watchlist' && l.is_default);
          if (watchlistList) {
            setAllListItemsDataWithSortInfo(queryClient, watchlistList.id, (old) => {
              if (!old) return old;
              return {
                ...old,
                pages: old.pages.map((page) => ({
                  ...page,
                  items: page.items.map((item) =>
                    item.media_id === mediaId && item.media_type === mediaType && item.id.startsWith('temp-')
                      ? { ...item, id: data.item.id }
                      : item
                  ),
                })),
              };
            });
          }
        }

        libraryStore.confirmMutation(mutationKey, mutation.id, Date.now() - startTime, data.duplicate);

        // Phase 4B: Invalidate React Query cache after successful mutation
        invalidateDefaultListCache('watchlist');

        return mutation.id;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          // Mutation was superseded - don't rollback, return null (callers ignore)
          return null;
        }
        // Rollback and throw - callers must handle with try/catch
        libraryStore.rollbackMutation(
          mutationKey,
          mutation.id,
          err instanceof Error ? err.message : 'Unknown error'
        );
        throw err;
      }
    },
    [session?.access_token, user, state.watchlist, invalidateDefaultListCache, optimisticPatchDefaultListCache]
  );

  // Toggle favorites status (optimistic)
  // TASK A: Now accepts enrichment for instant metadata display
  const toggleFavorites = useCallback(
    async (mediaId: number, mediaType: MediaType, mediaTitle: string, mediaPoster?: string, mediaYear?: number, enrichment?: MediaEnrichment) => {
      if (!session?.access_token || !user) return null;

      const key = getMediaKey(mediaId, mediaType);
      const mutationKey = getMutationKey('favorites', mediaId, mediaType);
      const isCurrentlyFavorite = state.favorites.has(key);
      const currentItem = state.favorites.get(key);
      const desiredInList = !isCurrentlyFavorite;

      // OUTBOX: Persist intent BEFORE mutation (survives refresh/navigation)
      // CRITICAL: Use slug as listId for stable keying (last-intent-wins)
      enqueueIntent(user.id, {
        listId: 'favorites', // Stable key - NOT UUID (would break last-intent-wins)
        listSlug: 'favorites',
        isDefaultList: true,
        mediaId,
        mediaType,
        mediaTitle,
        mediaPoster,
        mediaYear,
        desiredInList,
        itemId: currentItem?.itemId,
      });

      const snapshot: StateSnapshot = {
        capturedAt: Date.now(),
        mutationKey,
        favorites: isCurrentlyFavorite,
      };

      const mutation = libraryStore.createMutation(mutationKey, isCurrentlyFavorite ? 'remove' : 'add', snapshot);
      if (!mutation) return null;

      libraryStore.optimisticUpdate('favorites', mediaId, mediaType, !isCurrentlyFavorite);

      // Patch React Query cache for instant WatchlistPage appearance
      // TASK A: Pass enrichment for full metadata display
      optimisticPatchDefaultListCache('favorites', mediaId, mediaType, !isCurrentlyFavorite, mediaTitle, mediaPoster, mediaYear, enrichment);

      const startTime = Date.now();

      try {
        let response: Response;

        if (isCurrentlyFavorite && currentItem?.itemId) {
          // Remove from favorites - use itemId-only endpoint (no watchlistId needed)
          response = await fetch(`${API_BASE_URL}/watchlist-items/${currentItem.itemId}`, {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              'Idempotency-Key': mutation.id,
            },
            signal: mutation.abortController.signal,
          });
        } else {
          response = await fetch(`${API_BASE_URL}/watchlists/quick-add`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.access_token}`,
              'Idempotency-Key': mutation.id,
            },
            body: JSON.stringify({
              slug: 'favorites',
              media_id: mediaId,
              media_type: mediaType,
              media_title: mediaTitle,
              media_poster: mediaPoster,
              media_year: mediaYear,
            }),
            signal: mutation.abortController.signal,
          });
        }

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || `HTTP ${response.status}`);
        }

        const data = await response.json();

        if (!isCurrentlyFavorite && data.item?.id) {
          libraryStore.updateItemId('favorites', mediaId, mediaType, data.item.id);
          // Patch React Query list-items cache: replace temp ID with real server ID
          const favoritesList = queryClient.getQueryData<WatchlistMeta[]>(
            queryKeys.lists.mine(user.id)
          )?.find((l: WatchlistMeta) => l.slug === 'favorites' && l.is_default);
          if (favoritesList) {
            setAllListItemsDataWithSortInfo(queryClient, favoritesList.id, (old) => {
              if (!old) return old;
              return {
                ...old,
                pages: old.pages.map((page) => ({
                  ...page,
                  items: page.items.map((item) =>
                    item.media_id === mediaId && item.media_type === mediaType && item.id.startsWith('temp-')
                      ? { ...item, id: data.item.id }
                      : item
                  ),
                })),
              };
            });
          }
        }

        libraryStore.confirmMutation(mutationKey, mutation.id, Date.now() - startTime, data.duplicate);

        // Phase 4B: Invalidate React Query cache after successful mutation
        invalidateDefaultListCache('favorites');

        return mutation.id;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          // Mutation was superseded - don't rollback, return null (callers ignore)
          return null;
        }
        // Rollback and throw - callers must handle with try/catch
        libraryStore.rollbackMutation(
          mutationKey,
          mutation.id,
          err instanceof Error ? err.message : 'Unknown error'
        );
        throw err;
      }
    },
    [session?.access_token, user, state.favorites, invalidateDefaultListCache, optimisticPatchDefaultListCache]
  );

  // Set rating (optimistic)
  const setRating = useCallback(
    async (mediaId: number, mediaType: MediaType, rating: number) => {
      if (!session?.access_token || !user) return null;

      const key = getMediaKey(mediaId, mediaType);
      const mutationKey = getMutationKey('rating', mediaId, mediaType);
      const currentRating = state.ratings.get(key) ?? null;

      const snapshot: StateSnapshot = {
        capturedAt: Date.now(),
        mutationKey,
        rating: currentRating,
      };

      const mutation = libraryStore.createMutation(mutationKey, 'set', snapshot);
      if (!mutation) return null;

      libraryStore.optimisticRatingUpdate(mediaId, mediaType, rating);

      const startTime = Date.now();

      try {
        const response = await fetch(`${API_BASE_URL}/ratings`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
            'Idempotency-Key': mutation.id,
          },
          body: JSON.stringify({
            media_id: mediaId,
            media_type: mediaType,
            rating,
          }),
          signal: mutation.abortController.signal,
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || `HTTP ${response.status}`);
        }

        libraryStore.confirmMutation(mutationKey, mutation.id, Date.now() - startTime);
        return mutation.id;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return null;
        }
        libraryStore.rollbackMutation(
          mutationKey,
          mutation.id,
          err instanceof Error ? err.message : 'Unknown error'
        );
        return null;
      }
    },
    [session?.access_token, user, state.ratings]
  );

  // Remove rating (optimistic)
  const removeRating = useCallback(
    async (mediaId: number, mediaType: MediaType) => {
      if (!session?.access_token || !user) return null;

      const key = getMediaKey(mediaId, mediaType);
      const mutationKey = getMutationKey('rating', mediaId, mediaType);
      const currentRating = state.ratings.get(key) ?? null;

      if (currentRating === null) return null; // Nothing to remove

      const snapshot: StateSnapshot = {
        capturedAt: Date.now(),
        mutationKey,
        rating: currentRating,
      };

      const mutation = libraryStore.createMutation(mutationKey, 'remove', snapshot);
      if (!mutation) return null;

      libraryStore.optimisticRatingUpdate(mediaId, mediaType, null);

      const startTime = Date.now();

      try {
        const response = await fetch(`${API_BASE_URL}/ratings/${mediaType}/${mediaId}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Idempotency-Key': mutation.id,
          },
          signal: mutation.abortController.signal,
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || `HTTP ${response.status}`);
        }

        libraryStore.confirmMutation(mutationKey, mutation.id, Date.now() - startTime);
        return mutation.id;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return null;
        }
        libraryStore.rollbackMutation(
          mutationKey,
          mutation.id,
          err instanceof Error ? err.message : 'Unknown error'
        );
        return null;
      }
    },
    [session?.access_token, user, state.ratings]
  );

  // Follow system
  const isFollowing = useCallback(
    (userId: string) => {
      return state.followingUsers.has(userId);
    },
    [state.followingUsers]
  );

  const toggleFollow = useCallback(
    async (targetUserId: string): Promise<string | null> => {
      if (!session?.access_token || !user) return null;

      // Can't follow yourself
      if (targetUserId === user.id) return null;

      const viewerId = user.id;
      const wasFollowing = state.followingUsers.has(targetUserId);
      const desiredFollowing = !wasFollowing;
      const mutationId = crypto.randomUUID();
      const delta = wasFollowing ? -1 : 1;

      // OUTBOX: Persist intent BEFORE optimistic UI (survives refresh)
      // Must await to guarantee IndexedDB write completes before any UI/navigation
      try {
        await enqueueFollowIntent(viewerId, targetUserId, desiredFollowing);
      } catch (err) {
        // If IndexedDB fails (Safari private mode, quota, etc), do NOT crash.
        // Proceed best-effort without replay guarantee.
        if (import.meta.env.DEV) console.warn('[OUTBOX] enqueueFollowIntent failed:', err);
      }

      // A) Snapshot affected profile cache entries for rollback
      // Only look at cached profile queries and patch ones matching targetUserId or viewerId
      const previousProfiles = queryClient
        .getQueriesData<{ id?: string; followers_count?: number; following_count?: number }>({
          queryKey: ['profile'],
          exact: false,
        })
        .filter(([, data]) => data?.id === targetUserId || data?.id === viewerId);

      // B) Optimistic patch to profile caches (only existing entries)
      for (const [key, data] of previousProfiles) {
        if (!data || typeof data !== 'object') continue;

        if (data.id === targetUserId) {
          // Target user gains/loses a follower
          const prev = Number(data.followers_count ?? 0);
          queryClient.setQueryData(key, { ...data, followers_count: Math.max(0, prev + delta) });
        } else if (data.id === viewerId) {
          // Viewer gains/loses a following
          const prev = Number(data.following_count ?? 0);
          queryClient.setQueryData(key, { ...data, following_count: Math.max(0, prev + delta) });
        }
      }

      // Optimistic update to followingUsers Set
      libraryStore.optimisticFollowUpdate(targetUserId, !wasFollowing);

      // Broadcast to other tabs
      libraryStore.broadcast({
        type: wasFollowing ? 'unfollow_user' : 'follow_user',
        targetUserId,
      });

      // B2) Snapshot + optimistic patch followers/following infinite query caches
      type FollowPage = { users: FollowUser[]; next_cursor: string | null; has_more: boolean };
      const previousFollowQueries: [readonly unknown[], InfiniteData<FollowPage> | undefined][] = [];

      // Determine is_mutual for follow case (does target already follow viewer?)
      // Track confidence: if followStatus cache is missing, preserve existing row value
      let mutualKnown = false;
      let isMutual = false;
      if (!wasFollowing) {
        const followStatusData = queryClient.getQueryData<FollowStatusData>(
          queryKeys.users.followStatus(targetUserId, viewerId)
        );
        if (followStatusData) {
          mutualKnown = true;
          isMutual = followStatusData.follows_viewer === true;
        }
      }

      // Find all cached followers/following infinite queries
      const followQueries = queryClient.getQueryCache().findAll({
        predicate: (q) => {
          const k = q.queryKey;
          return Array.isArray(k) && k[0] === 'users' && (k[2] === 'followers' || k[2] === 'following');
        },
      });

      for (const query of followQueries) {
        const key = query.queryKey;
        const data = queryClient.getQueryData<InfiniteData<FollowPage>>(key);
        if (!data?.pages) continue;

        // Snapshot for rollback
        previousFollowQueries.push([key, data]);

        const isViewerFollowingList = key[1] === viewerId && key[2] === 'following';

        const newPages = data.pages.map((page) => {
          let users = page.users.map((u) => {
            if (u.id === targetUserId) {
              return {
                ...u,
                is_following: desiredFollowing,
                // On unfollow: always false. On follow: use known status, or preserve existing row value if unknown.
                is_mutual: desiredFollowing
                  ? (mutualKnown ? isMutual : u.is_mutual)
                  : false,
                followers_count: Math.max(0, u.followers_count + delta),
              };
            }
            if (u.id === viewerId) {
              return {
                ...u,
                following_count: Math.max(0, u.following_count + delta),
              };
            }
            return u;
          });

          // On unfollow: remove target from viewer's following list
          if (wasFollowing && isViewerFollowingList) {
            users = users.filter((u) => u.id !== targetUserId);
          }

          return { ...page, users };
        });

        queryClient.setQueryData<InfiniteData<FollowPage>>(key, {
          ...data,
          pages: newPages,
        });
      }

      try {
        const response = await fetch(`${API_BASE_URL}/users/${targetUserId}/follow`, {
          method: wasFollowing ? 'DELETE' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
            'Idempotency-Key': `${wasFollowing ? 'unfollowUser' : 'followUser'}:${targetUserId}`,
          },
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({} as Record<string, unknown>));
          const err = new Error((data.error as string) || `HTTP ${response.status}`);
          (err as Error & { code?: string }).code = data.code as string;
          throw err;
        }

        const data = await response.json();

        // Verify success
        if (!data.success) {
          throw new Error('Follow operation failed');
        }

        // Remove follow intent from outbox (server confirmed)
        await removeIntent(viewerId, `follow:${targetUserId}`);

        // C) On success: invalidate active profile queries for reconciliation
        // Uses predicate to target only the two affected profiles (bounded, no refetch storm)
        queryClient.invalidateQueries({
          predicate: (q) => {
            if (!Array.isArray(q.queryKey) || q.queryKey[0] !== 'profile') return false;
            const d = q.state.data as { id?: string } | undefined;
            return d?.id === targetUserId || d?.id === viewerId;
          },
          refetchType: 'active',
        });

        // D) Invalidate followers/following infinite query caches for eventual consistency
        // Target's followers list (gained/lost a follower) and viewer's following list (followed/unfollowed someone)
        queryClient.invalidateQueries({
          queryKey: ['users', targetUserId, 'followers'],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: ['users', viewerId, 'following'],
          exact: false,
        });

        // Return mutationId on success (FollowButton uses this to show toast)
        return mutationId;
      } catch (err) {
        // E) Rollback profile caches from snapshot
        for (const [key, data] of previousProfiles) {
          queryClient.setQueryData(key, data);
        }

        // Rollback followers/following infinite query caches from snapshot
        for (const [key, data] of previousFollowQueries) {
          queryClient.setQueryData(key, data);
        }

        // Rollback followingUsers Set
        libraryStore.rollbackFollowUpdate(targetUserId, wasFollowing);

        // Broadcast rollback to other tabs
        libraryStore.broadcast({
          type: wasFollowing ? 'follow_user' : 'unfollow_user',
          targetUserId,
        });

        console.error('Follow toggle failed:', err);

        // Surface SOCIAL_TIER_REQUIRED so caller can show upgrade CTA
        const code = (err as Error & { code?: string }).code;
        if (code === 'SOCIAL_TIER_REQUIRED') return 'SOCIAL_TIER_REQUIRED';

        return null;
      }
    },
    [session?.access_token, user, state.followingUsers, queryClient]
  );

  const hasPendingMutation = useCallback((key: MutationKey) => {
    return libraryStore.hasPendingMutation(key);
  }, []);

  // Derived counts from membership state (for ProfilePage)
  const watchedCount = state.watched.size;
  const watchlistCount = state.watchlist.size;
  const favoritesCount = state.favorites.size;

  const getCustomListItemCount = useCallback(
    (listId: string) => {
      // Count items in customListItems that belong to this list
      let count = 0;
      for (const key of state.customListItems.keys()) {
        if (key.startsWith(`${listId}:`)) {
          const item = state.customListItems.get(key);
          // Don't count tombstones or placeholders
          if (item && !item._tombstone) {
            count++;
          }
        }
      }
      return count;
    },
    [state.customListItems]
  );

  // Bounded read-only getter for watchlist IDs (for filtering without exposing raw Map)
  const getWatchlistIds = useCallback(
    (mediaType?: 'movie' | 'tv', limit: number = 2000): number[] => {
      const ids: number[] = [];
      for (const key of state.watchlist.keys()) {
        if (ids.length >= limit) break;
        // Key format: "movie:123" or "tv:456"
        const [type, idStr] = key.split(':');
        if (mediaType && type !== mediaType) continue;
        const id = parseInt(idStr, 10);
        if (!isNaN(id)) ids.push(id);
      }
      return ids;
    },
    [state.watchlist]
  );

  // Sync a custom list item without toggling (for AddMovieModal hybrid approach)
  // Uses existing store methods - does NOT add new helpers to LibraryStore
  const syncCustomListItem = useCallback(
    (listId: string, mediaId: number, mediaType: MediaType, itemId: string) => {
      const key = getCustomListItemKey(listId, mediaId, mediaType);
      const existing = state.customListItems.get(key);

      if (!existing) {
        // Item not in context - add it using existing optimisticCustomListUpdate
        libraryStore.optimisticCustomListUpdate(listId, mediaId, mediaType, true, itemId, false);
      } else if (existing.itemId !== itemId) {
        // Item exists but with different/missing itemId - update using existing method
        libraryStore.updateCustomListItemId(listId, mediaId, mediaType, itemId);
      }
      // If item exists with same itemId, no-op (already in sync)
    },
    [state.customListItems]
  );

  const value: UserLibraryContextValue = useMemo(
    () => ({
      isHydrated: state.isHydrated,
      userId: state.userId,
      defaultListIds: state.defaultListIds,
      isWatched,
      isInWatchlist,
      isInFavorites,
      getRating,
      getItemId,
      watchedCount,
      watchlistCount,
      favoritesCount,
      getCustomListItemCount,
      getWatchlistIds,
      isInCustomList,
      getCustomLists,
      getCustomListItemId,
      toggleWatched,
      toggleWatchlist,
      toggleFavorites,
      setRating,
      removeRating,
      toggleCustomList,
      fetchCustomListItems,
      syncCustomListItem,
      isFollowing,
      toggleFollow,
      softSync,
      hardRehydrate,
      hasPendingMutation,
      pendingMutationCount: libraryStore.pendingMutationCount,
    }),
    [
      state.isHydrated,
      state.userId,
      state.defaultListIds,
      isWatched,
      isInWatchlist,
      isInFavorites,
      getRating,
      getItemId,
      watchedCount,
      watchlistCount,
      favoritesCount,
      getCustomListItemCount,
      getWatchlistIds,
      isInCustomList,
      getCustomLists,
      getCustomListItemId,
      toggleWatched,
      toggleWatchlist,
      toggleFavorites,
      setRating,
      removeRating,
      isFollowing,
      toggleFollow,
      toggleCustomList,
      fetchCustomListItems,
      syncCustomListItem,
      softSync,
      hardRehydrate,
      hasPendingMutation,
    ]
  );

  return <UserLibraryContext.Provider value={value}>{children}</UserLibraryContext.Provider>;
}

export function useUserLibrary() {
  const context = useContext(UserLibraryContext);
  if (!context) {
    throw new Error('useUserLibrary must be used within a UserLibraryProvider');
  }
  return context;
}
